import { round } from "@shared/utils/numbers";
import { getIsDarkMode } from "utils/misc";
import { filterAnnotations, getChartYAxisRange } from "@shared/utils/charts";
import { typography } from "@shared/common/Themes/typography";
import { CHART_CUSTOM_POSITION_KEY } from "common/constants";
import { SECONDS_IN_DAY, SECONDS_IN_WEEK } from "@shared/common/constants.js";

const TIME_CHART_GRID_DASH = [2, 4];

export const getDataPointFromTooltipContext = (context) => {
  const { dataPoints } = context.tooltip;
  return dataPoints?.length ? dataPoints[0] : {};
};

const drawVerticalLine = (chart, x, hole) => {
  const isDarkMode = getIsDarkMode();
  const topY = chart.scales["y"].top;
  const bottomY = chart.scales["y"].bottom;
  const { ctx } = chart;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x, topY);
  if (hole) {
    const { top: holeTop, bottom: holeBottom } = hole;
    if (holeTop) {
      ctx.lineTo(x, holeTop);
      if (holeBottom) {
        ctx.moveTo(x, holeBottom);
        ctx.lineTo(x, bottomY);
      }
    }
  }
  ctx.lineWidth = 0.75;
  ctx.strokeStyle = isDarkMode ? "#7A90BF4D" : "#2E3D5C7F";
  ctx.setLineDash([2, 4]);
  ctx.stroke();
  ctx.restore();
};

function roundRect(ctx, x, y, width, height, radius, fill) {
  if (typeof radius === "undefined") {
    radius = 5;
  }
  if (typeof radius === "number") {
    radius = { tl: radius, tr: radius, br: radius, bl: radius };
  } else {
    var defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
    for (var side in defaultRadius) {
      radius[side] = radius[side] || defaultRadius[side];
    }
  }
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + radius.tl, y);
  ctx.lineTo(x + width - radius.tr, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
  ctx.lineTo(x + width, y + height - radius.br);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius.br,
    y + height
  );
  ctx.lineTo(x + radius.bl, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
  ctx.lineTo(x, y + radius.tl);
  ctx.quadraticCurveTo(x, y, x + radius.tl, y);
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  ctx.restore();
}

const setTextStylesAndRunCallback = (
  ctx,
  callback,
  color = "white",
  fontSize = 10
) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.letterSpacing = "-0.65px";
  ctx.font = `700 ${fontSize}px ${typography.fontFamily}`;
  const returnValue = callback && callback();
  ctx.restore();
  return returnValue;
};

const getTagDimensions = (textMetrics, padding) => {
  const tagWidth =
    textMetrics.actualBoundingBoxLeft +
    textMetrics.actualBoundingBoxRight +
    padding[0] * 2 +
    2;
  const tagHeight =
    textMetrics.actualBoundingBoxAscent +
    textMetrics.actualBoundingBoxAscent +
    padding[1] * 2;
  return { height: tagHeight, width: tagWidth };
};

const drawYAxisTag = (
  ctx,
  text,
  yValue,
  color,
  padding,
  yScale,
  chartArea,
  alignRight = false,
  fillRect = true,
  textColor
) => {
  const textMetrics = setTextStylesAndRunCallback(
    ctx,
    () => ctx.measureText(text),
    fillRect ? textColor : color,
    fillRect ? undefined : 11
  );
  const { height, width } = getTagDimensions(textMetrics, padding);
  const tagYTop = yValue - height / 2;
  const tagYBottom = yValue + height / 2;
  if (tagYTop > yScale.bottom || tagYBottom < 0) return;
  const x = alignRight ? chartArea.left + chartArea.width - width : 0;
  fillRect &&
    roundRect(ctx, x, tagYTop, Math.max(width, yScale.right), height, 4, color);
  setTextStylesAndRunCallback(
    ctx,
    () => ctx.fillText(text, x + padding[0], yValue),
    fillRect ? textColor : color,
    fillRect ? undefined : 12
  );
};

export const yAxisTagsPlugin = {
  id: "yAxisTagsPlugin",
  afterDraw: (chart, args, options) => {
    const { annotations } = options;
    const yScale = chart.scales["y"];
    annotations &&
      annotations.forEach((annotation) => {
        const { yMin: value, distance, showDistanceAbove } = annotation;
        const annotationYPixel = yScale.getPixelForValue(value);
        drawYAxisTag(
          chart.ctx,
          value,
          annotationYPixel,
          annotation.borderColor,
          [8, 4],
          yScale,
          chart.chartArea,
          false,
          true,
          annotation.textColor
        );
        if (distance) {
          drawYAxisTag(
            chart.ctx,
            distance,
            showDistanceAbove ? annotationYPixel - 8 : annotationYPixel + 10,
            annotation.borderColor,
            [8, 0],
            yScale,
            chart.chartArea,
            true,
            false
          );
        }
      });
  },
};

export const lineChartHoverPlugin = {
  id: "lineChartHoverPlugin",
  afterDraw: function (chart) {
    if (chart?.tooltip?._active?.length) {
      const activePoint = chart.tooltip._active[0];
      const { hoverRadius } = activePoint.element.options;
      const { x: pointX, y: pointY } = activePoint.element;
      drawVerticalLine(chart, pointX, {
        top: pointY - hoverRadius,
        bottom: pointY + hoverRadius,
      });
    }
  },
};

export const barChartHoverPlugin = {
  id: "barChartHoverPlugin",
  afterDraw: function (chart) {
    if (chart?.tooltip?._active?.length) {
      const activeBar = chart.tooltip._active[0];
      const { x: barX, y: barY } = activeBar.element;
      drawVerticalLine(chart, barX, { top: barY });
    }
  },
};

export const getTimeChartOptions = (
  tooltipGenerator,
  precision,
  annotations,
  extendToAnnotations,
  data,
  multiData,
  isMinified,
  appTheme,
  height,
  numOfXTicks,
  isStacked,
  isWebsocket,
  xMin,
  xMax,
  valueSuffix,
  hideXAxis,
  hideYAxis,
  ticksColor,
  numOfYTicks,
  lastDataPointLabel,
  hideTooltip,
  axisOverlapData,
  gridColor,
  includeBounds,
  addPadding
) => {
  const numYTicks = numOfYTicks || 5;
  const normalizedPrecision = Number.isFinite(precision)
    ? precision
    : undefined;
  const getXAxisUnit = () => {
    const points = data ?? multiData?.[0].data;
    if (!points?.length) return;
    const diff = points?.[points.length - 1]?.rawX - points[0]?.rawX;
    if (diff > 3 * SECONDS_IN_DAY && diff <= SECONDS_IN_WEEK) {
      return "day";
    }
  };

  const options = {
    animation: false,
    events:
      isMinified || hideTooltip
        ? []
        : ["mousemove", "mouseout", "touchmove", "touchend", "click"],
    layout: {
      padding: {
        left: axisOverlapData ? -16 : 0,
      },
    },
    scales: {
      x: {
        min: xMin,
        max: xMax,
        stacked: isStacked,
        display: !isMinified && !hideXAxis,
        type: "time",
        time: {
          parser: "YYYY/MM/DD HH:mm:ss",
          unit: getXAxisUnit(),
        },
        ticks: {
          display: !isMinified && !hideXAxis,
          maxTicksLimit: numOfXTicks || 5,
          maxRotation: 0,
          minRotation: 0,
          color: ticksColor ?? appTheme.colors.chartTicksColor,
          font: {
            weight: 500,
            family: appTheme.fontFamily,
          },
        },
        afterBuildTicks: (axis) => {
          const firstTick = axis.ticks[0];
          if (isWebsocket && firstTick) {
            const difference = (Date.now() - firstTick.value) * 0.9;
            axis.ticks = axis.ticks.filter(
              (tick) => tick.value - firstTick.value <= difference
            );
          }
        },
        grid: {
          display: false,
        },
        border: {
          color: appTheme.colors.lightestBorderGrey,
        },
      },
      y: {
        stacked: isStacked,
        display: !isMinified && !hideYAxis,
        ticks: {
          padding: axisOverlapData ? 8 : 0,
          mirror: axisOverlapData,
          display: !isMinified && !hideYAxis,
          count: numYTicks,
          includeBounds: includeBounds,
          precision: normalizedPrecision,
          color: ticksColor ?? appTheme.colors.chartTicksColor,
          font: {
            weight: 500,
            family: appTheme.typography.fontFamily,
          },
          callback: (value) => {
            const roundedValue =
              normalizedPrecision === undefined
                ? value
                : round(value, normalizedPrecision);
            const parsedValue = roundedValue.toLocaleString(undefined, {
              maximumFractionDigits: normalizedPrecision,
            });
            return valueSuffix ? parsedValue + valueSuffix : parsedValue;
          },
        },
        grid: {
          drawTicks: !axisOverlapData,
          display: !isMinified && !hideYAxis,
          color: gridColor ?? appTheme.colors.chartGridColor,
        },
        border: {
          dash: () => TIME_CHART_GRID_DASH,
          color: appTheme.colors.lightestBorderGrey,
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: tooltipGenerator,
        position: CHART_CUSTOM_POSITION_KEY,
      },
      legend: {
        display: false,
      },
      yAxisTagsPlugin: {},
      datalabels: {
        display: (context) =>
          !!lastDataPointLabel &&
          context.dataIndex === context.dataset.data.length - 1,
        align: "right",
        color: appTheme.colors.primaryBlue,
        formatter: () => lastDataPointLabel,
        font: {
          family: appTheme.typography.fontFamily,
          size: 10,
        },
      },
    },
  };
  const filteredAnnotations = filterAnnotations(
    data,
    annotations,
    extendToAnnotations
  );
  if (filteredAnnotations) {
    options.plugins.annotation = {
      annotations: filteredAnnotations,
    };
    options.plugins.yAxisTagsPlugin.annotations = filteredAnnotations;
  }

  if (!isStacked) {
    let [minYValue, maxYValue] = getChartYAxisRange(
      data,
      multiData,
      filteredAnnotations,
      height
    );
    const yScale = options.scales.y;
    if (!minYValue && !maxYValue) {
      yScale.min = isMinified ? -0.02 : 0;
      yScale.max = 1;
    } else if (!isMinified) {
      yScale.min = minYValue;
      yScale.max = maxYValue;
    }
    if (precision === 0 && maxYValue < numYTicks) {
      yScale.max = numYTicks - 1;
    }
    if (addPadding) {
      yScale.min = yScale.min - yScale.min * 0.06;
      yScale.max = yScale.max + yScale.max * 0.06;
    }
  }

  if (extendToAnnotations && data.length) {
    options.plugins.yAxisTagsPlugin.extendToAnnotations = true;
  }

  return options;
};

export const getLowAndHighPrices = (data) => {
  const priceData = data?.map((dataPoint) => dataPoint[1]) || [];
  return [Math.min(...priceData), Math.max(...priceData)].map((price) =>
    isFinite(price) ? price : 0
  );
};
